import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useState } from 'react';

import { fcpCompleteAtom, lcpCompleteAtom } from '@frontend/atoms';

const FALLBACK_TIMEOUT = 3000;

export function usePerformanceMetrics() {
  const lcpComplete = useAtomValue(lcpCompleteAtom);
  const fcpComplete = useAtomValue(fcpCompleteAtom);
  const setLcpComplete = useSetAtom(lcpCompleteAtom);
  const setFcpComplete = useSetAtom(fcpCompleteAtom);
  const [idleComplete, setIdleComplete] = useState(false);

  useEffect(() => {
    if (lcpComplete && fcpComplete) return;

    if (!('PerformanceObserver' in window)) {
      setLcpComplete(true);
      setFcpComplete(true);
      return;
    }

    let lcpObserver: PerformanceObserver | null = null;
    let fcpObserver: PerformanceObserver | null = null;
    let lcpTimeout: ReturnType<typeof setTimeout> | null = null;
    let fcpTimeout: ReturnType<typeof setTimeout> | null = null;

    if (!lcpComplete) {
      lcpObserver = new PerformanceObserver(entryList => {
        if (entryList.getEntries().length > 0) {
          setLcpComplete(true);
          lcpObserver?.disconnect();
          if (lcpTimeout) clearTimeout(lcpTimeout);
        }
      });
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });

      lcpTimeout = setTimeout(() => {
        setLcpComplete(true);
        lcpObserver?.disconnect();
      }, FALLBACK_TIMEOUT);
    }

    if (!fcpComplete) {
      fcpObserver = new PerformanceObserver(entryList => {
        if (entryList.getEntries().length > 0) {
          setFcpComplete(true);
          fcpObserver?.disconnect();
          if (fcpTimeout) clearTimeout(fcpTimeout);
        }
      });
      fcpObserver.observe({ type: 'paint', buffered: true });

      fcpTimeout = setTimeout(() => {
        setFcpComplete(true);
        fcpObserver?.disconnect();
      }, FALLBACK_TIMEOUT);
    }

    return () => {
      lcpObserver?.disconnect();
      fcpObserver?.disconnect();
      if (lcpTimeout) clearTimeout(lcpTimeout);
      if (fcpTimeout) clearTimeout(fcpTimeout);
    };
  }, [lcpComplete, fcpComplete, setLcpComplete, setFcpComplete]);

  useEffect(() => {
    if (idleComplete) return;

    if (!('requestIdleCallback' in window)) {
      setIdleComplete(true);
      return;
    }

    const id = window.requestIdleCallback(() => setIdleComplete(true));

    return () => window.cancelIdleCallback(id);
  }, [idleComplete]);

  return { lcpComplete, fcpComplete, idleComplete };
}
